/**
 * CrewTrustPage ✦ — /crew/trust
 *
 * EventDrop Crew Trust Centre. Designed to feel like Discord T&S
 * or modern fintech trust pages — premium, glassmorphic, neon
 * dark — never like a government safeguarding portal.
 *
 * Eight scrollable sections + animated trust pills + reframing
 * "Why Event Experience Matters" closer. Every claim is short
 * copy + iconography; legal-PDF energy is explicitly avoided.
 *
 * Backend integration:
 *   POST /api/crew/safeguarding/report — sends a concern to the
 *   DSL inbox. Open-to-anyone (anonymous OK) — safeguarding access
 *   must never gate on auth.
 */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ShieldCheck, SealCheck, ChatCircleDots, UsersThree,
  Warning, HandHeart, GraduationCap, Sparkle, Lock, ArrowRight,
  Eye, BellRinging, FirstAid, Lightning, CaretDown, CaretUp,
  PaperPlaneTilt, CheckCircle,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.04)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const GOLD = '#FFD166';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const TRUST_PILLS = [
  { id: 'verified',   label: 'Verified suppliers',         icon: SealCheck,      color: BLUE   },
  { id: 'messaging',  label: 'On-platform messaging',      icon: ChatCircleDots, color: PURPLE },
  { id: 'report',     label: 'Report concerns instantly',  icon: Warning,        color: PINK   },
  { id: 'under18',    label: 'Under-18 safeguards',        icon: ShieldCheck,    color: BLUE   },
  { id: 'zero',       label: 'Zero tolerance policy',      icon: FirstAid,       color: PINK   },
];

const Pill = ({ icon: Icon, label, color }) => (
  <div
    data-testid={`trust-pill-${label.toLowerCase().replace(/\s+/g, '-')}`}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '8px 14px', borderRadius: 999,
      background: 'rgba(255,255,255,0.05)',
      border: `1px solid ${color}55`,
      color: TEXT, fontSize: 12.5, fontWeight: 700,
      whiteSpace: 'nowrap', flexShrink: 0,
      boxShadow: `0 0 14px ${color}22 inset`,
    }}
  >
    <Icon size={13} weight="fill" color={color} />
    {label}
  </div>
);

const SectionCard = ({ id, eyebrow, eyebrowColor = PINK, title, icon: Icon, accent = PURPLE, children }) => (
  <section
    id={id}
    data-testid={`trust-section-${id}`}
    style={{
      padding: 24, borderRadius: 22,
      background: PANEL, border: `1px solid ${PANEL_BORDER}`,
      marginBottom: 14,
      scrollMarginTop: 24,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      {Icon && (
        <div style={{
          width: 38, height: 38, borderRadius: 12,
          background: `linear-gradient(135deg, ${accent}, ${PINK})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 8px 20px ${accent}33`,
        }}>
          <Icon size={18} weight="fill" color="#fff" />
        </div>
      )}
      <p style={{
        margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.32em',
        textTransform: 'uppercase',
        background: `linear-gradient(135deg, ${eyebrowColor}, ${BLUE})`,
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        {eyebrow}
      </p>
    </div>
    <h2 style={{
      margin: '0 0 14px', fontFamily: FONT_STACK,
      fontSize: 22, fontWeight: 900, letterSpacing: '-0.01em', color: TEXT,
    }}>
      {title}
    </h2>
    <div style={{ color: TEXT_MUTED, fontSize: 14, lineHeight: 1.65 }}>
      {children}
    </div>
  </section>
);

const Tick = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '4px 0' }}>
    <CheckCircle size={16} weight="fill" color={BLUE} style={{ flexShrink: 0, marginTop: 2 }} />
    <span style={{ color: TEXT, fontSize: 13.5, lineHeight: 1.5 }}>{text}</span>
  </div>
);

const Expander = ({ summary, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testId}
      style={{
        marginTop: 14, padding: 12, borderRadius: 12,
        background: 'rgba(108,59,255,0.08)', border: `1px solid ${PURPLE}33`,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        style={{
          width: '100%', background: 'transparent', border: 'none',
          cursor: 'pointer', color: TEXT, fontFamily: FONT_STACK,
          fontSize: 13.5, fontWeight: 800, textAlign: 'left',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <span>{summary}</span>
        {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
      </button>
      {open && (
        <div style={{ marginTop: 10, color: TEXT_MUTED, fontSize: 13, lineHeight: 1.6 }}>
          {children}
        </div>
      )}
    </div>
  );
};

const ReportConcernCard = () => {
  const formRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState({ category: 'safeguarding', email: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!body.message.trim()) {
      setError('Add a few words so we can investigate.');
      return;
    }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/crew/safeguarding/report`, body);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="trust-report-card"
      style={{
        padding: 22, borderRadius: 22,
        background: `linear-gradient(135deg, ${PINK}22 0%, ${PURPLE}1A 100%)`,
        border: `1px solid ${PINK}55`,
        marginBottom: 14,
        boxShadow: `0 12px 30px ${PINK}22`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Warning size={20} weight="fill" color={PINK} />
        <p style={{
          margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: PINK,
        }}>
          Report a concern
        </p>
      </div>
      <h2 style={{
        margin: '0 0 8px', fontFamily: FONT_STACK,
        fontSize: 22, fontWeight: 900, color: TEXT, letterSpacing: '-0.01em',
      }}>
        Something didn't feel right? Tell us immediately ✦
      </h2>
      <p style={{ margin: '0 0 14px', color: TEXT_MUTED, fontSize: 13.5, lineHeight: 1.55 }}>
        A message, a supplier, an event setup — anything. Reports go straight to our
        Designated Safeguarding Lead. You can stay completely anonymous.
      </p>

      {sent ? (
        <div data-testid="trust-report-success" style={{
          padding: '16px 18px', borderRadius: 14,
          background: 'rgba(51,195,255,0.08)', border: `1px solid ${BLUE}55`,
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <CheckCircle size={20} weight="fill" color={BLUE} style={{ marginTop: 1, flexShrink: 0 }} />
          <div>
            <p style={{ margin: 0, color: TEXT, fontSize: 14, fontWeight: 800, lineHeight: 1.45 }}>
              Thanks — our safeguarding team has received your report ✦
            </p>
            <p style={{ margin: '4px 0 0', color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.5 }}>
              A real person will review this within 24 hours. If it's urgent, email{' '}
              <a href="mailto:safeguarding@eventdrop.co" style={{ color: BLUE, fontWeight: 700 }}>
                safeguarding@eventdrop.co
              </a>.
            </p>
          </div>
        </div>
      ) : (
        <>
          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="trust-report-open"
              style={{
                padding: '12px 22px', borderRadius: 999, border: 'none',
                cursor: 'pointer', color: TEXT, fontFamily: FONT_STACK,
                fontSize: 13.5, fontWeight: 900,
                background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                boxShadow: `0 10px 24px ${PURPLE}55`,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <PaperPlaneTilt size={13} weight="fill" /> Open the report form
            </button>
          ) : (
            <form ref={formRef} onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['safeguarding', 'communication', 'supplier', 'other'].map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setBody({ ...body, category: c })}
                    data-testid={`trust-report-cat-${c}`}
                    style={{
                      padding: '7px 12px', borderRadius: 999,
                      border: `1px solid ${body.category === c ? PINK : PANEL_BORDER}`,
                      background: body.category === c
                        ? `linear-gradient(135deg, ${PURPLE}55, ${PINK}55)`
                        : 'transparent',
                      color: body.category === c ? TEXT : TEXT_MUTED,
                      cursor: 'pointer', fontFamily: FONT_STACK,
                      fontSize: 11.5, fontWeight: 700, textTransform: 'capitalize',
                    }}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <input
                type="email"
                placeholder="Your email (optional)"
                value={body.email}
                onChange={(e) => setBody({ ...body, email: e.target.value })}
                data-testid="trust-report-email"
                style={{
                  padding: '12px 14px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}`,
                  color: TEXT, fontFamily: FONT_STACK, fontSize: 14, outline: 'none',
                }}
              />
              <textarea
                placeholder="Tell us what happened — as much detail as you're comfortable with."
                value={body.message}
                onChange={(e) => setBody({ ...body, message: e.target.value })}
                data-testid="trust-report-message"
                rows={4}
                maxLength={2000}
                style={{
                  padding: '12px 14px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}`,
                  color: TEXT, fontFamily: FONT_STACK, fontSize: 14, outline: 'none',
                  resize: 'vertical', lineHeight: 1.5,
                }}
              />
              {error && (
                <p data-testid="trust-report-error" style={{ margin: 0, color: PINK, fontSize: 12.5 }}>
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="trust-report-submit"
                style={{
                  padding: '12px 22px', borderRadius: 999, border: 'none',
                  cursor: busy ? 'wait' : 'pointer', color: TEXT,
                  fontFamily: FONT_STACK, fontSize: 13.5, fontWeight: 900,
                  background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                  boxShadow: `0 10px 24px ${PURPLE}55`,
                  opacity: busy ? 0.7 : 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <PaperPlaneTilt size={13} weight="fill" />
                {busy ? 'Sending…' : 'Send to the DSL ✦'}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
};

const CrewTrustPage = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="crew-trust-page" style={{
      minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
      padding: 'clamp(20px, 5vw, 56px) clamp(16px, 5vw, 32px) 80px',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(circle at 18% 12%, ${BLUE}1A 0%, transparent 42%),
                     radial-gradient(circle at 82% 84%, ${PURPLE}22 0%, transparent 45%)`,
      }} />

      <div style={{ maxWidth: 760, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <button
          onClick={() => navigate('/crew')}
          data-testid="trust-back"
          style={{
            background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 24,
          }}
        >
          <ArrowLeft size={14} weight="bold" /> EventDrop Crew
        </button>

        {/* ── Hero ── */}
        <section style={{ marginBottom: 28 }}>
          <p style={{
            margin: '0 0 8px', fontSize: 12, fontWeight: 800, letterSpacing: '0.36em',
            textTransform: 'uppercase',
            background: `linear-gradient(135deg, ${BLUE}, ${GOLD})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            ✦ Trust Centre
          </p>
          <h1 style={{
            margin: '0 0 14px', fontFamily: FONT_STACK,
            fontSize: 'clamp(34px, 6vw, 52px)', fontWeight: 900,
            letterSpacing: '-0.02em', lineHeight: 1.05, color: TEXT,
          }}>
            Building Confidence Safely ✦
          </h1>
          <p style={{
            margin: '0 0 22px', maxWidth: 560,
            fontSize: 16.5, color: TEXT_MUTED, lineHeight: 1.6,
          }}>
            EventDrop Crew is designed to help young people gain real-world
            experience in a safe, supported environment. Here's how we keep it that way.
          </p>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 8,
          }}>
            {TRUST_PILLS.map((p) => (
              <Pill key={p.id} icon={p.icon} label={p.label} color={p.color} />
            ))}
          </div>
        </section>

        {/* ── Section 1: Our Commitment ── */}
        <SectionCard
          id="commitment"
          eyebrow="Our commitment"
          eyebrowColor={BLUE}
          title="A platform built around the people on it"
          icon={HandHeart}
          accent={BLUE}
        >
          <Tick text="Confidence-building over hustle culture — real experience, real growth." />
          <Tick text="Respectful environments — every supplier signs the same code of conduct." />
          <Tick text="Professionalism modelled by suppliers, mirrored back to crew." />
          <Tick text="Swift safeguarding response — concerns reviewed within 24 hours." />
        </SectionCard>

        {/* ── Section 2: Supplier Verification ── */}
        <SectionCard
          id="suppliers"
          eyebrow="Supplier verification"
          eyebrowColor={BLUE}
          title="Real businesses, not anonymous accounts"
          icon={SealCheck}
          accent={PURPLE}
        >
          <Tick text="Identity checks for every supplier before they can post a shift." />
          <Tick text="Business verification — registered companies, public-facing presence." />
          <Tick text="Continuous platform monitoring — patterns of concern flag instantly." />
          <Tick text="Crew review system — references and reliability scores stay visible." />
          <Tick text="Optional enhanced verification tier for higher-trust opportunities." />
          <div
            data-testid="trust-badge-tiers"
            style={{
              marginTop: 16,
              display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            }}
          >
            {[
              { id: 'verified',  label: 'Verified Supplier',   sub: 'ID + business checked', accent: BLUE,   icon: SealCheck },
              { id: 'trusted',   label: 'Trusted Supplier',    sub: 'Repeat-booked by crew', accent: PURPLE, icon: ShieldCheck },
              { id: 'enhanced',  label: 'Enhanced Verified',   sub: 'In-person ID confirmed', accent: GOLD,   icon: Sparkle },
            ].map((tier) => (
              <div
                key={tier.id}
                data-testid={`trust-badge-tier-${tier.id}`}
                style={{
                  padding: '12px 14px', borderRadius: 16,
                  background: `linear-gradient(135deg, ${tier.accent}1A, ${PURPLE}10)`,
                  border: `1px solid ${tier.accent}55`,
                  boxShadow: `0 8px 20px ${tier.accent}22`,
                  position: 'relative', overflow: 'hidden',
                }}
              >
                <div style={{
                  position: 'absolute', top: -18, right: -18, width: 60, height: 60,
                  borderRadius: '50%', filter: 'blur(20px)',
                  background: tier.accent, opacity: 0.18,
                }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <tier.icon size={16} weight="fill" color={tier.accent}
                    style={{ filter: `drop-shadow(0 0 6px ${tier.accent}66)` }} />
                  <p style={{
                    margin: 0, fontWeight: 900, fontSize: 13.5, color: TEXT,
                    letterSpacing: '-0.005em',
                  }}>
                    {tier.label}
                  </p>
                </div>
                <p style={{ margin: 0, fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.4 }}>
                  {tier.sub}
                </p>
              </div>
            ))}
          </div>
          <p style={{
            margin: '12px 0 0', fontSize: 12.5, color: TEXT_MUTED, fontStyle: 'italic',
          }}>
            Suppliers earn these badges over time. Crew see them at a glance.
          </p>
          <div
            data-testid="trust-verified-badge-preview"
            style={{
              marginTop: 14, padding: '10px 16px', borderRadius: 999,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: `linear-gradient(135deg, ${BLUE}22, ${PURPLE}22)`,
              border: `1px solid ${BLUE}55`, color: TEXT,
              fontWeight: 800, fontSize: 13,
            }}
          >
            <SealCheck size={14} weight="fill" color={BLUE} /> ✓ Verified Supplier
          </div>
        </SectionCard>

        {/* ── Section 3: Communication Safety ── */}
        <SectionCard
          id="messaging"
          eyebrow="Communication safety"
          eyebrowColor={PINK}
          title="Keeping communication on EventDrop protects everyone ✦"
          icon={Lock}
          accent={PINK}
        >
          <Tick text="All messaging stays on-platform — moderation can see context if needed." />
          <Tick text="Report or end any conversation in two taps from the chat menu." />
          <Tick text="Audit logging — every send, block, and report is timestamped and stored." />
          <Tick text="Rate limits stop spam before it lands." />
          <Tick text="Off-platform contact requests can be flagged — we'll review them." />
        </SectionCard>

        {/* ── Section 4: Under 18 ── */}
        <SectionCard
          id="under18"
          eyebrow="Under 18 protection"
          eyebrowColor={BLUE}
          title="Designed for young crew, with safeguards baked in"
          icon={ShieldCheck}
          accent={BLUE}
        >
          <Tick text="Parents and guardians actively encouraged to know what's going on." />
          <Tick text="Inappropriate behaviour = instant removal. No second chances." />
          <Tick text="No private unsupervised meetings — events are public, professional environments." />
          <Tick text="Concerns investigated immediately — the DSL has direct line into the moderation queue." />
          <Tick text="Age-aware tagging on opportunities so beginners and under-18s see suitable shifts first." />
        </SectionCard>

        {/* ── Section 5: Reporting Concerns ── */}
        <SectionCard
          id="report-flow"
          eyebrow="Reporting concerns"
          eyebrowColor={PINK}
          title="What happens when you report"
          icon={BellRinging}
          accent={PINK}
        >
          <div
            data-testid="trust-flow"
            style={{
              display: 'grid', gap: 10, marginTop: 4,
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            }}
          >
            {[
              { n: '1', t: 'You tap report', s: 'Anywhere on platform.' },
              { n: '2', t: 'Logged instantly', s: 'Goes to the DSL queue.' },
              { n: '3', t: 'Reviewed', s: 'Within 24 hours.' },
              { n: '4', t: 'Action taken', s: 'You get an update.' },
            ].map((step) => (
              <div
                key={step.n}
                data-testid={`trust-flow-step-${step.n}`}
                style={{
                  padding: 14, borderRadius: 14,
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${PANEL_BORDER}`,
                  textAlign: 'center',
                }}
              >
                <div style={{
                  width: 28, height: 28, margin: '0 auto 8px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 900, fontSize: 13, color: TEXT,
                  boxShadow: `0 4px 12px ${PURPLE}33`,
                }}>
                  {step.n}
                </div>
                <p style={{ margin: '0 0 4px', fontWeight: 800, fontSize: 13, color: TEXT }}>{step.t}</p>
                <p style={{ margin: 0, color: TEXT_MUTED, fontSize: 11.5 }}>{step.s}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Section 6: Zero Tolerance (red-accent) ── */}
        <section
          data-testid="trust-section-zero-tolerance"
          style={{
            padding: 24, borderRadius: 22, marginBottom: 14,
            background: `linear-gradient(135deg, rgba(255,60,172,0.10) 0%, rgba(255,90,90,0.06) 100%)`,
            border: `1px solid ${PINK}55`,
            boxShadow: `0 10px 30px ${PINK}22`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: `linear-gradient(135deg, ${PINK}, #FF6A6A)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 8px 20px ${PINK}55`,
            }}>
              <FirstAid size={18} weight="fill" color="#fff" />
            </div>
            <p style={{
              margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.32em',
              textTransform: 'uppercase', color: PINK,
            }}>
              Zero tolerance
            </p>
          </div>
          <h2 style={{
            margin: '0 0 8px', fontFamily: FONT_STACK,
            fontSize: 22, fontWeight: 900, color: TEXT, letterSpacing: '-0.01em',
          }}>
            What ends your time on EventDrop immediately
          </h2>
          <p style={{ margin: '0 0 14px', color: TEXT_MUTED, fontSize: 13.5, lineHeight: 1.55 }}>
            We protect crew first — always. Any of these and the account is gone.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[
              'Harassment',
              'Grooming behaviour',
              'Discrimination',
              'Coercion',
              'Bypassing safeguards',
            ].map((t) => (
              <span
                key={t}
                data-testid={`trust-zero-${t.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  padding: '7px 12px', borderRadius: 999,
                  background: 'rgba(255,60,172,0.16)',
                  border: `1px solid ${PINK}66`,
                  color: TEXT, fontSize: 12, fontWeight: 800,
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </section>

        {/* ── Section 7: Parents & Guardians ── */}
        <SectionCard
          id="parents"
          eyebrow="Parents & guardians"
          eyebrowColor={BLUE}
          title="What you need to know about EventDrop Crew"
          icon={UsersThree}
          accent={PURPLE}
        >
          <p style={{ margin: '0 0 12px', color: TEXT, fontSize: 14, lineHeight: 1.6 }}>
            EventDrop Crew helps young people get real-world experience at events —
            from photobooth assistants to wedding setup, festival runners, and beyond.
            Every supplier is verified, every conversation is on-platform, and every concern
            is reviewed by a Designated Safeguarding Lead.
          </p>
          <Tick text="Real venues, professional environments — never private addresses." />
          <Tick text="Communication stays inside the app so it can be reviewed if needed." />
          <Tick text="Crew control which opportunities they apply for — nothing is automatic." />
          <Tick text="One-tap reporting puts you in touch with our DSL within hours." />
          <Expander summary="What's the supervision expectation?" testId="trust-expander-supervision">
            Suppliers run their events professionally and crew are part of the staffing team.
            Crew are never asked to attend alone or in private settings — every shift is a
            structured public event with an identifiable supplier.
          </Expander>
          <Expander summary="How can I check on shifts my child accepts?" testId="trust-expander-checking">
            Crew can share their on-platform thread with you — supplier name, event title,
            time, and location are visible inside every conversation. We're working on a
            dedicated Parent Dashboard for our next release.
          </Expander>
        </SectionCard>

        {/* ── Section 8: DSL ── */}
        <SectionCard
          id="dsl"
          eyebrow="Designated Safeguarding Lead"
          eyebrowColor={BLUE}
          title="Who reviews your reports"
          icon={GraduationCap}
          accent={BLUE}
        >
          <div
            data-testid="trust-dsl-card"
            style={{
              padding: 16, borderRadius: 14, marginTop: 4,
              background: `linear-gradient(135deg, ${BLUE}14, ${PURPLE}10)`,
              border: `1px solid ${BLUE}33`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 46, height: 46, borderRadius: '50%',
                background: `linear-gradient(135deg, ${BLUE}, ${PURPLE})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 22px ${BLUE}44`,
              }}>
                <ShieldCheck size={20} weight="fill" color="#fff" />
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: TEXT }}>
                  EventDrop Safeguarding Team
                </p>
                <p style={{ margin: '2px 0 0', color: TEXT_MUTED, fontSize: 12.5 }}>
                  Reviews every report. Responds within 24 hours.
                </p>
              </div>
            </div>
            <p style={{ margin: '12px 0 0', color: TEXT_MUTED, fontSize: 13, lineHeight: 1.55 }}>
              Reach the team directly via the form below, or email{' '}
              <a href="mailto:safeguarding@eventdrop.co"
                 style={{ color: BLUE, textDecoration: 'none', fontWeight: 700 }}>
                safeguarding@eventdrop.co
              </a>.
            </p>
          </div>
        </SectionCard>

        {/* ── Report concern ── */}
        <ReportConcernCard />

        {/* ── Why event experience matters ── */}
        <section
          data-testid="trust-section-why-matters"
          style={{
            padding: 24, borderRadius: 22, marginBottom: 14,
            background: `linear-gradient(135deg, ${PURPLE}22 0%, ${BLUE}1A 100%)`,
            border: `1px solid ${PURPLE}55`,
            boxShadow: `0 12px 32px ${PURPLE}33`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: `linear-gradient(135deg, ${GOLD}, ${PINK})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 8px 20px ${GOLD}33`,
            }}>
              <Sparkle size={18} weight="fill" color="#fff" />
            </div>
            <p style={{
              margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.32em',
              textTransform: 'uppercase', color: GOLD,
            }}>
              Why this matters
            </p>
          </div>
          <h2 style={{
            margin: '0 0 14px', fontFamily: FONT_STACK,
            fontSize: 24, fontWeight: 900, color: TEXT, letterSpacing: '-0.01em',
          }}>
            Why event experience matters ✦
          </h2>
          <p style={{ margin: '0 0 14px', color: TEXT_MUTED, fontSize: 14.5, lineHeight: 1.65 }}>
            Real shifts at real events are some of the most formative experiences a
            young person can have. Done well, they build:
          </p>
          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          }}>
            {[
              { l: 'Confidence',     i: Lightning, c: PINK   },
              { l: 'Communication',  i: ChatCircleDots, c: BLUE },
              { l: 'Teamwork',       i: UsersThree, c: PURPLE },
              { l: 'Resilience',     i: ShieldCheck, c: BLUE },
              { l: 'Professionalism',i: SealCheck, c: GOLD },
            ].map((s) => (
              <div
                key={s.l}
                data-testid={`trust-why-${s.l.toLowerCase()}`}
                style={{
                  padding: 14, borderRadius: 14,
                  background: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${s.c}33`,
                  textAlign: 'center',
                }}
              >
                <s.i size={20} weight="fill" color={s.c}
                  style={{ filter: `drop-shadow(0 0 6px ${s.c}55)` }} />
                <p style={{ margin: '8px 0 0', fontWeight: 800, fontSize: 13, color: TEXT }}>
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── What's next ── */}
        <SectionCard
          id="future"
          eyebrow="Coming soon"
          eyebrowColor={GOLD}
          title="What we're building next"
          icon={Eye}
          accent={GOLD}
        >
          <Tick text="Trusted Supplier tiers — visible track records, repeat-booking history." />
          <Tick text="Enhanced verification — optional in-person ID checks for higher-trust shifts." />
          <Tick text="Parent Dashboards — opt-in visibility into upcoming shifts and supplier identity." />
          <Tick text="School & college partnerships — structured pathways into event experience." />
        </SectionCard>

        {/* ── CTA back to feed ── */}
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <button
            onClick={() => navigate('/crew/feed')}
            data-testid="trust-cta-feed"
            style={{
              padding: '13px 24px', borderRadius: 999, border: 'none',
              cursor: 'pointer', color: TEXT, fontFamily: FONT_STACK,
              fontSize: 14, fontWeight: 900,
              background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
              boxShadow: `0 12px 30px ${PURPLE}55`,
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            Explore opportunities <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrewTrustPage;
